/**
 * Client-side exceptions that are not actionable Fleetwire bugs.
 * Dropped in Sentry beforeSend (see plugins/sentry.ts).
 */

import type {ErrorEvent} from '@sentry/vue';

function getErrorMessage(reason: unknown): string {
    if (reason instanceof Error) {
        return reason.message;
    }

    if (typeof reason === 'string') {
        return reason;
    }

    return reason != null ? String(reason) : '';
}

function getEventExceptionMessage(event?: ErrorEvent): string {
    return event?.exception?.values?.[0]?.value ?? (typeof event?.message === 'string' ? event.message : '');
}

function stackFrames(event?: ErrorEvent) {
    return event?.exception?.values?.[0]?.stacktrace?.frames ?? [];
}

type AxiosLike = {
    isAxiosError?: boolean;
    code?: string;
    message?: string;
    response?: unknown;
};

function asAxiosLike(reason: unknown): AxiosLike | null {
    if (!reason || typeof reason !== 'object') {
        return null;
    }

    const err = reason as AxiosLike;

    return err.isAxiosError === true ? err : null;
}

function stackMentionsNativeBridge(event: ErrorEvent): boolean {
    return stackFrames(event).some((frame) => {
        const fn = frame.function ?? '';
        const file = frame.filename ?? '';

        return /postMessage|sendPostMessage|dispatchToBridge|sendDataToNative|messageHandlers/i.test(fn)
            || /iabjs:|webkit\.messageHandlers/i.test(file);
    });
}

/**
 * Offline / aborted XHR with no HTTP response. Keep real 4xx/5xx.
 * (VUE-7F0, VUE-93J, VUE-8RG, …)
 */
export function isIgnorableAxiosTransportNoise(reason: unknown): boolean {
    const err = asAxiosLike(reason);

    if (err) {
        if (err.response != null) {
            return false;
        }

        if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
            return true;
        }

        const msg = (err.message ?? '').trim();

        return msg === 'Network Error' || /^timeout of \d+ms exceeded$/i.test(msg);
    }

    const message = getErrorMessage(reason).trim();

    return message === 'Network Error' || /^timeout of \d+ms exceeded$/i.test(message);
}

/**
 * Legacy Google Analytics / iOS Chrome onerror noise: bare message "ga"
 * with no app frames (VUE-9N2).
 *
 * Also: Chrome Mobile iOS reports "Maximum call stack size exceeded" via
 * onerror with only `undefined:N:N` frames (VUE-8MY) — not Fleetwire recursion.
 *
 * Also: Vue teardown races (`vnode` null) during HMR / overlay unmount (VUE-96V).
 *
 * Also: Instagram / WKWebView / Android IAB bridge noise (VUE-96B, VUE-9N4, VUE-9HK).
 */
export function isIgnorableClientNoise(reason: unknown, event?: ErrorEvent): boolean {
    const message = (getErrorMessage(reason) || getEventExceptionMessage(event)).trim();

    if (message === 'ga' || message === 'fa') {
        return true;
    }

    if (/Cannot destructure property ['"]type['"] of ['"]?vnode['"]? as it is null/i.test(message)) {
        return true;
    }

    if (/window\.webkit\.messageHandlers/i.test(message)) {
        return true;
    }

    if (/Java object is gone/i.test(message)) {
        return true;
    }

    if (
        event
        && /The object does not support the operation or argument/i.test(message)
        && stackMentionsNativeBridge(event)
    ) {
        return true;
    }

    // Only when Sentry gave us an event — without frames we must not drop real recursion.
    if (event && /maximum call stack size exceeded/i.test(message) && hasOnlyUndefinedStackFrames(event)) {
        return true;
    }

    return false;
}

/**
 * Stack frames that are only `undefined` / anonymous — typical of iOS Chrome
 * onerror noise with no usable app frames (VUE-8MY).
 */
export function hasOnlyUndefinedStackFrames(event: ErrorEvent): boolean {
    const frames = stackFrames(event);

    if (frames.length === 0) {
        return true;
    }

    return frames.every((frame) => {
        const filename = frame.filename ?? '';

        return filename === '' || filename === 'undefined' || filename === '<anonymous>';
    });
}
